import { collection, query, where, getDocs, getDoc, doc, startAfter, limit, orderBy } from 'firebase/firestore';
import { db } from '../../data/base';

// Status mapping da italiano a inglese
const statusMapping = {
    "In Pubblicazione": "In Publication",
    "Rev. Cliente": "Client Rev.",
    "Rev. Spaarkly": "Spaarkly Rev.",
    "Rev. Modellista": "Modelist Rev.",
    "Incompleto": "Incomplete",
    "Pubblicato": "Published"
};

// Funzione helper per applicare i filtri rispettando il limite di Firebase
const buildFirebaseFilters = (filters) => {
    let baseConditions = [where("status", "==", "Pubblicato")];
    let postQueryFilters = {}; // Inizializza sempre per consistenza

    if (!filters) {
        baseConditions.push(where("mainBrandRef", "!=", null));
        return { baseConditions, postQueryFilters };
    }

    // 🎯 GESTIONE UNIFICATA FILTRI
    // La ricerca testuale e showNews ora sono gestiti come filtri post-query.
    if (filters.searchTerm) {
        postQueryFilters.searchTerm = filters.searchTerm.toUpperCase().trim();
    }
    if (filters.showNews) {
        postQueryFilters.showNews = true;
    }

    // Applica sempre i filtri diretti (non array)
    if (filters.brands && filters.brands.length > 0) {
        if (filters.brands.length <= 10) {
            // Se i brand sono 10 o meno, usa la query 'in' di Firebase
            baseConditions.push(where("mainBrandRef", "in", filters.brands));
        } else {
            // Se sono più di 10, gestisci il filtro lato client
            postQueryFilters.brands = filters.brands;
        }
    }

    if (filters.age && filters.age.length > 0 && filters.age.length <= 10) {
        baseConditions.push(where("age", "in", filters.age));
    }

    // Raccoglie tutti i possibili array filters
    const arrayFilters = [];

    if (filters.lines && filters.lines.length > 0) {
        arrayFilters.push({
            type: 'lines',
            field: 'list_line_tags',
            values: filters.lines,
            priority: 1
        });
    }

    if (filters.combinedListTags && filters.combinedListTags.length > 0) {
        arrayFilters.push({
            type: 'combinedListTags',
            field: 'list_tags',
            values: filters.combinedListTags,
            priority: 2
        });
    }

    if (filters.sizes && filters.sizes.length > 0) {
        arrayFilters.push({
            type: 'sizes',
            field: 'list_size_tags',
            values: filters.sizes,
            priority: 3
        });
    }

    if (filters.frameColors && filters.frameColors.length > 0) {
        arrayFilters.push({
            type: 'frameColors',
            field: 'list_frame_color_tags',
            values: filters.frameColors,
            priority: 3
        });
    }

    // Se non ci sono array filters, applica solo la condizione base
    if (arrayFilters.length === 0) {
        if (!filters.brands || filters.brands.length === 0) {
            baseConditions.push(where("mainBrandRef", "!=", null));
        }
        return { baseConditions, postQueryFilters };
    }

    // Ordina per priorità e dimensione
    arrayFilters.sort((a, b) => {
        if (a.priority !== b.priority) return a.priority - b.priority;
        return a.values.length - b.values.length;
    });

    // Applica il filtro con la priorità più alta a Firebase
    const primaryFilter = arrayFilters[0];
    if (primaryFilter.values.length <= 10) {
        baseConditions.push(where(primaryFilter.field, "array-contains-any", primaryFilter.values));
        // Aggiungi i filtri rimanenti a postQueryFilters
        arrayFilters.slice(1).forEach(filter => {
            postQueryFilters[filter.type] = filter.values;
        });
    } else {
        // Se anche il filtro primario è troppo grande, sposta tutto in post-query
        arrayFilters.forEach(filter => {
            postQueryFilters[filter.type] = filter.values;
        });
    }

    // Se non abbiamo applicato nessun brand filter, aggiungi la condizione base
    if (!filters.brands || filters.brands.length === 0) {
        baseConditions.push(where("mainBrandRef", "!=", null));
    }

    return { baseConditions, postQueryFilters };
};

// Funzione per ottenere il conteggio totale degli SKU, con supporto per filtri
export const getTotalSkusCount = async (filters = null) => {
    try {
        const productsRef = collection(db, "Products");
        const { baseConditions, postQueryFilters } = buildFirebaseFilters(filters);

        const skuQuery = query(productsRef, ...baseConditions);
        const querySnapshot = await getDocs(skuQuery);

        let products = [];
        querySnapshot.forEach((doc) => {
            const productData = doc.data();
            // 🎯 COSTRUISCI OGGETTO COMPLETO per i filtri post-query
            products.push({
                id: doc.id,
                list_line_tags: productData.list_line_tags || [],
                list_tags: productData.list_tags || [],
                list_size_tags: productData.list_size_tags || [],
                list_frame_color_tags: productData.list_frame_color_tags || [],
                availableVariants: productData.availableVariants || 0,
                lastPublishedInARSLibrary: productData.lastPublishedInARSLibrary,
                variants_map: productData.variants_map || [],
                searchable_modelName: (productData.name || productData.modelName || '').toUpperCase(),
                searchable_sku: (productData.sku || '').toUpperCase(),
                searchable_ean: (productData.ean || '').toUpperCase(),
                searchable_upc: (productData.upc || '').toUpperCase(),
            });
        });

        // Applica post-query filters se necessario
        let finalProducts = products;
        if (postQueryFilters && Object.keys(postQueryFilters).length > 0) {
            const { applyPostQueryFilters } = await import('../data/ARSCatalogFiltersData');
            const result = applyPostQueryFilters(products, postQueryFilters);
            finalProducts = result.products;
        }

        let totalSkus = 0;
        finalProducts.forEach(product => {
            totalSkus += product.availableVariants;
        });

        return totalSkus;
    } catch (error) {
        console.error("Error getting total SKUs count:", error);
        throw error;
    }
};

// Funzione per ottenere il conteggio totale dei prodotti, con supporto per filtri
export const getProductsCount = async (filters = null) => {
    try {
        const productsRef = collection(db, "Products");
        const { baseConditions, postQueryFilters } = buildFirebaseFilters(filters);

        const countQuery = query(productsRef, ...baseConditions);
        const querySnapshot = await getDocs(countQuery);

        // Se non ci sono post-query filters, restituisci direttamente la dimensione
        if (!postQueryFilters || Object.keys(postQueryFilters).length === 0) {
            return querySnapshot.size;
        }

        // Altrimenti, carica i prodotti e applica i filtri post-query
        let products = [];
        querySnapshot.forEach((doc) => {
            const productData = doc.data();
            // 🎯 COSTRUISCI OGGETTO COMPLETO per i filtri post-query
            products.push({
                id: doc.id,
                list_line_tags: productData.list_line_tags || [],
                list_tags: productData.list_tags || [],
                list_size_tags: productData.list_size_tags || [],
                list_frame_color_tags: productData.list_frame_color_tags || [],
                lastPublishedInARSLibrary: productData.lastPublishedInARSLibrary,
                variants_map: productData.variants_map || [],
                searchable_modelName: (productData.name || productData.modelName || '').toUpperCase(),
                searchable_sku: (productData.sku || '').toUpperCase(),
                searchable_ean: (productData.ean || '').toUpperCase(),
                searchable_upc: (productData.upc || '').toUpperCase(),
            });
        });

        const { applyPostQueryFilters } = await import('../data/ARSCatalogFiltersData');
        const result = applyPostQueryFilters(products, postQueryFilters);
        return result.products.length;
    } catch (error) {
        console.error("Error getting products count:", error);
        throw error;
    }
};

// Recupera prodotti con i loro brand associati e carica le prime varianti per la ricerca
export const fetchProducts = async (page = 1, pageSize = 10, lastVisibleDocument = null, filters = null) => {
    try {
        const validPageSize = Math.min(Math.max(pageSize, 10), 50);
        const productsRef = collection(db, "Products");

        // 🎯 LOGICA FILTRI UNIFICATA
        const { baseConditions, postQueryFilters } = buildFirebaseFilters(filters);
        const hasPostQueryFilters = postQueryFilters && Object.keys(postQueryFilters).length > 0;

        // Se abbiamo filtri che richiedono la scansione completa (ricerca, showNews, etc.),
        // non possiamo usare la paginazione di Firebase.
        const requiresFullScan = hasPostQueryFilters;

        let productsQuery;
        if (requiresFullScan) {
            // Carica tutti i documenti che soddisfano le condizioni di base
            productsQuery = query(productsRef, ...baseConditions, orderBy("lastUpdate", "desc"));
        } else {
            // Altrimenti, usa la paginazione standard
            const queryConditions = [
                ...baseConditions,
                orderBy("lastUpdate", "desc"),
                limit(validPageSize)
            ];
            if (lastVisibleDocument) {
                queryConditions.push(startAfter(lastVisibleDocument));
            }
            productsQuery = query(productsRef, ...queryConditions);
        }

        const productsSnapshot = await getDocs(productsQuery);
        let productsData = [];

        // Carica i dati dei prodotti e dei brand associati
        const brandPromises = productsSnapshot.docs.map(async (productDoc) => {
            const productData = productDoc.data();
            if (!productData.mainBrandRef) return null;

            const brandRef = doc(db, "MainBrands", productData.mainBrandRef);
            const brandSnap = await getDoc(brandRef);
            const brandData = brandSnap.exists() ? brandSnap.data() : { nome_brand: 'N/A' };

            return {
                key: productDoc.id,
                id: productDoc.id,
                modelName: productData.name || productData.modelName || 'N/A',
                status: statusMapping[productData.status] || productData.status,
                lastUpdate: productData.lastUpdate || 'N/A',
                brandId: productData.mainBrandRef,
                brandName: brandData.nome_brand || brandData.nameBrand || 'N/A',
                imgUrl: productData.urlImage || productData.imgUrl,
                list_variants: productData.list_variants || [],
                list_line_tags: productData.list_line_tags || [],
                list_tags: productData.list_tags || [],
                list_size_tags: productData.list_size_tags || [],
                list_frame_color_tags: productData.list_frame_color_tags || [],
                gender: productData.gender,
                modelType: productData.modelType,
                typology: productData.typology,
                sku: productData.sku,
                ean: productData.ean,
                upc: productData.upc,
                mainBrandRef: productData.mainBrandRef,
                age: productData.age || 'adult',
                availableVariants: productData.availableVariants || 0,
                lastPublishedInARSLibrary: productData.lastPublishedInARSLibrary,
                variants_map: productData.variants_map || [],
                // Dati per la ricerca
                searchable_modelName: (productData.name || productData.modelName || '').toUpperCase(),
                searchable_sku: (productData.sku || '').toUpperCase(),
                searchable_ean: (productData.ean || '').toUpperCase(),
                searchable_upc: (productData.upc || '').toUpperCase(),
            };
        });

        productsData = (await Promise.all(brandPromises)).filter(p => p !== null);

        let finalProducts = productsData;
        let matchedVariants = {};

        // Applica i filtri post-query se necessario
        if (requiresFullScan) {
            const { applyPostQueryFilters } = await import('../data/ARSCatalogFiltersData');
            const result = applyPostQueryFilters(productsData, postQueryFilters);
            finalProducts = result.products;
            matchedVariants = result.matchedVariants || {};
        }

        // Gestione della paginazione MANUALE se è stata eseguita una scansione completa
        let paginatedProducts = finalProducts;
        let newLastVisible = null;
        let hasMore = false;

        if (requiresFullScan) {
            const startIndex = (page - 1) * validPageSize;
            const endIndex = startIndex + validPageSize;
            paginatedProducts = finalProducts.slice(startIndex, endIndex);
            hasMore = endIndex < finalProducts.length;
            // In questo scenario, 'lastVisible' non è un documento, ma potremmo usare l'offset
            // o l'ultimo prodotto della pagina per riferimento, se necessario. Per ora, null va bene.
        } else {
            newLastVisible = productsSnapshot.docs.length > 0 ? productsSnapshot.docs[productsSnapshot.docs.length - 1] : null;
            hasMore = productsSnapshot.docs.length === validPageSize;
        }

        return {
            products: paginatedProducts,
            lastVisible: newLastVisible,
            hasMore: hasMore,
            matchedVariants: matchedVariants, // Restituisci le varianti matchate
            totalResults: finalProducts.length, // Conteggio totale dopo i filtri
        };

    } catch (error) {
        console.error("Error fetching products:", error);
        throw error;
    }
};

export const fetchProductsByVariantFilters = async (filters) => {
    try {
        const { combinedListSizeColorTags, brands } = filters;

        if (!combinedListSizeColorTags || combinedListSizeColorTags.length === 0) {
            return { products: [], variants: {}, totalProducts: 0, totalSkus: 0 };
        }

        const variantsRef = collection(db, "Variants");
        const variantConditions = [
            where("list_size_color_tags", "array-contains-any", combinedListSizeColorTags),
        ];

        const variantsQuery = query(variantsRef, ...variantConditions);
        const variantsSnapshot = await getDocs(variantsQuery);

        if (variantsSnapshot.empty) {
            return { products: [], variants: {}, totalProducts: 0, totalSkus: 0 };
        }

        const foundVariants = [];
        const productRefs = new Set();
        variantsSnapshot.forEach(doc => {
            const variantData = doc.data();
            foundVariants.push({ id: doc.id, ...variantData });
            if (variantData.productRef) {
                productRefs.add(variantData.productRef);
            }
        });

        if (productRefs.size === 0) {
            return { products: [], variants: {}, totalProducts: 0, totalSkus: 0 };
        }

        let productIds = Array.from(productRefs);

        if (brands && brands.length > 0) {
            const brandFilteredProductIds = new Set();
            const productPromises = productIds.map(id => getDoc(doc(db, "Products", id)));
            const productSnaps = await Promise.all(productPromises);

            productSnaps.forEach(prodSnap => {
                if (prodSnap.exists()) {
                    const prodData = prodSnap.data();
                    if (brands.includes(prodData.mainBrandRef)) {
                        brandFilteredProductIds.add(prodSnap.id);
                    }
                }
            });
            productIds = Array.from(brandFilteredProductIds);
        }

        if (productIds.length === 0) {
            return { products: [], variants: {}, totalProducts: 0, totalSkus: 0 };
        }

        const finalProductPromises = productIds.map(id => fetchProductById(id));
        const productsData = (await Promise.all(finalProductPromises)).filter(p => p);

        const variantsByProduct = {};
        let finalSkus = 0;
        foundVariants.forEach(variant => {
            const productId = variant.productRef;
            if (productIds.includes(productId)) {
                if (!variantsByProduct[productId]) {
                    variantsByProduct[productId] = [];
                }
                variantsByProduct[productId].push(variant);
                finalSkus++;
            }
        });

        return {
            products: productsData,
            variants: variantsByProduct,
            totalProducts: productsData.length,
            totalSkus: finalSkus
        };

    } catch (error) {
        console.error("Error fetching products by variant filters:", error);
        throw error;
    }
};

// 🎯 FUNZIONE RIMOSSA: searchProductsAndVariants - la logica è ora in fetchProducts
// export const searchProductsAndVariants = async (searchTerm) => { ... };

// 🎯 RIMOSSA: fetchNews - ora showNews è gestito nel flusso unificato di fetchProducts


// Recupera varianti per ID direttamente dalla collezione Variants
export const fetchVariantsByIds = async (variantIds = []) => {
    if (!variantIds || variantIds.length === 0) return [];

    try {
        // Riduco la dimensione del batch da 10 a 5 per evitare troppe richieste simultanee
        const batchSize = 5;
        const results = [];

        // Crea blocchi di ID da elaborare in batch
        for (let i = 0; i < variantIds.length; i += batchSize) {
            const chunk = variantIds.slice(i, i + batchSize);

            // Elabora ogni blocco sequenzialmente
            const chunkPromises = chunk.map(async (variantId) => {
                try {
                    const variantRef = doc(db, "Variants", variantId);
                    const variantSnap = await getDoc(variantRef);

                    if (variantSnap.exists()) {
                        const variantData = variantSnap.data();

                        return {
                            id: variantSnap.id,
                            ...variantData,
                            status: statusMapping[variantData.status] || variantData.status
                        };
                    }
                    return null;
                } catch (error) {
                    console.error(`Error fetching variant ${variantId}:`, error);
                    return null;
                }
            });

            // Attendi il completamento del batch corrente prima di passare al successivo
            const batchResults = await Promise.all(chunkPromises);
            results.push(...batchResults.filter(v => v !== null));

            // Aumenta il ritardo tra i batch per evitare di sovraccaricare Firebase
            if (i + batchSize < variantIds.length) {
                // Aumento il delay da 300ms a 500ms
                await new Promise(resolve => setTimeout(resolve, 500));
            }
        }

        return results;
    } catch (error) {
        console.error("Error retrieving variants by IDs:", error);
        return [];
    }
};

// Recupera varianti per il campo productRef
export const fetchVariantsByProductRef = async (productId) => {
    try {
        const variantsQuery = query(
            collection(db, "Variants"),
            where("productRef", "==", productId),
        );

        const querySnapshot = await getDocs(variantsQuery);

        const productVariants = querySnapshot.docs.map(doc => {
            const variantData = doc.data();
            return {
                id: doc.id,
                ...variantData,
                status: statusMapping[variantData.status] || variantData.status
            };
        });

        return productVariants;
    } catch (error) {
        console.error("Error retrieving variants by productRef:", error);
        return [];
    }
};

// Recupera un singolo prodotto per ID
export const fetchProductById = async (productId) => {
    try {
        // Ottieni il riferimento al documento del prodotto
        const productRef = doc(db, "Products", productId);
        const productSnap = await getDoc(productRef);

        if (!productSnap.exists()) {
            throw new Error("Product not found");
        }

        const productData = productSnap.data();

        // Recupera i dati del brand
        let brandData = { nome_brand: 'N/A (Brand not found)' };

        if (productData.mainBrandRef) {
            const brandRef = doc(db, "MainBrands", productData.mainBrandRef);
            const brandSnap = await getDoc(brandRef);

            if (brandSnap.exists()) {
                brandData = brandSnap.data();
            }
        }

        // Costruisci e restituisci l'oggetto prodotto
        return {
            key: productId,
            id: productId,
            modelName: productData.name || productData.modelName || 'N/A',
            status: statusMapping[productData.status] || productData.status,
            lastUpdate: productData.lastUpdate || 'N/A',
            brandId: productData.mainBrandRef,
            brandName: brandData.nome_brand || brandData.nameBrand || 'N/A',
            imgUrl: productData.urlImage || productData.imgUrl,
            list_variants: productData.list_variants || [],
            list_line_tags: productData.list_line_tags || [],
            list_tags: productData.list_tags || [],
            // 🎯 NUOVO: Aggiungi i nuovi campi per i filtri
            list_size_tags: productData.list_size_tags || [],
            list_frame_color_tags: productData.list_frame_color_tags || [],
            gender: productData.gender,
            modelType: productData.modelType,
            typology: productData.typology,
            mainBrandRef: productData.mainBrandRef,
            // Codici di ricerca del prodotto
            sku: productData.sku,
            ean: productData.ean,
            upc: productData.upc,
            // Dati completi per la pagina di dettaglio
            rawData: productData,
            age: productData.age || 'adult',
            availableVariants: productData.availableVariants || 0,
            // 🎯 IMPORTANTE: Aggiungi il campo per il badge "New" sui prodotti
            lastPublishedInARSLibrary: productData.lastPublishedInARSLibrary,
        };
    } catch (error) {
        console.error("Error retrieving product:", error);
        throw error;
    }
}

export const fetchCatalogueProductById = async (productId) => {
    try {
        // Ottieni il riferimento al documento del prodotto
        const productRef = doc(db, "CataloguesProducts", productId);
        const productSnap = await getDoc(productRef);

        if (!productSnap.exists()) {
            throw new Error("Product not found");
        }

        const productData = productSnap.data();

        // Recupera i dati del brand
        let brandData = { nome_brand: 'N/A (Brand not found)' };

        if (productData.mainBrandRef) {
            const brandRef = doc(db, "MainBrands", productData.mainBrandRef);
            const brandSnap = await getDoc(brandRef);

            if (brandSnap.exists()) {
                brandData = brandSnap.data();
            }
        }

        // Costruisci e restituisci l'oggetto prodotto
        return {
            key: productId,
            id: productId,
            modelName: productData.name || productData.modelName || 'N/A',
            status: statusMapping[productData.status] || productData.status,
            lastUpdate: productData.lastUpdate || 'N/A',
            brandId: productData.mainBrandRef,
            brandName: brandData.nome_brand || brandData.nameBrand || 'N/A',
            imgUrl: productData.urlImage || productData.imgUrl,
            list_variants: productData.list_variants || [],
            list_line_tags: productData.list_line_tags || [],
            list_tags: productData.list_tags || [],
            gender: productData.gender,
            modelType: productData.modelType,
            typology: productData.typology,
            mainBrandRef: productData.mainBrandRef,
            // Codici di ricerca del prodotto
            sku: productData.sku,
            ean: productData.ean,
            upc: productData.upc,
            // Dati completi per la pagina di dettaglio
            rawData: productData
        };
    } catch (error) {
        console.error("Error retrieving product:", error);
        throw error;
    }
};

export const fetchCatalogueVariantsByProductRef = async (productId) => {
    try {
        const variantsQuery = query(
            collection(db, "CataloguesVariants"),
            where("catalogProductRef", "==", productId),
        );

        const querySnapshot = await getDocs(variantsQuery);

        const productVariants = querySnapshot.docs.map(doc => {
            const variantData = doc.data();
            return {
                id: doc.id,
                ...variantData,
                status: statusMapping[variantData.status] || variantData.status
            };
        });

        return productVariants;
    } catch (error) {
        console.error("Error retrieving variants by catalogProductRef:", error);
        return [];
    }
}; 